import { useState } from 'react';
import { Button, Input, ListBox, Modal, Select, Spinner, TextField } from '@heroui/react';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import type { Location, WorkerRow, VisitTemplate } from '@/lib/types';

interface CreateVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: VisitTemplate[];
  workers: WorkerRow[];
  locations: Location[];
  onTemplateChange: (templateId: string) => void;
  isCreating: boolean;
  error: string | null;
  onCreate: (payload: {
    visit_template_id: string;
    user_id: string;
    planned_at: string;
    location_id: string;
  }) => Promise<boolean>;
}

export function CreateVisitModal({
  isOpen,
  onClose,
  templates,
  workers,
  locations,
  onTemplateChange,
  isCreating,
  error,
  onCreate,
}: CreateVisitModalProps) {
  const [templateId, setTemplateId] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [plannedAt, setPlannedAt] = useState('');
  const [locationId, setLocationId] = useState('');

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  const handleTemplateChange = (key: string | number | null) => {
    const id = String(key ?? '');
    setTemplateId(id);
    // смена шаблона меняет клиента → список локаций и выбранная локация сбрасываются
    setLocationId('');
    if (id) onTemplateChange(id);
  };

  const handleSubmit = async () => {
    const created = await onCreate({
      visit_template_id: templateId,
      user_id: workerId,
      planned_at: plannedAt ? new Date(plannedAt).toISOString() : '',
      location_id: locationId,
    });
    if (created) {
      setTemplateId('');
      setWorkerId('');
      setPlannedAt('');
      setLocationId('');
      onClose();
    }
  };

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Container size="md">
        <Modal.Dialog className="sm:max-w-lg" aria-label="Создание визита">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Новый визит</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <Select
                className="w-full"
                variant="secondary"
                value={templateId}
                onChange={handleTemplateChange}
                placeholder="Выберите шаблон"
              >
                <FieldLabel>Шаблон</FieldLabel>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {templates.map((template) => (
                      <ListBox.Item key={template.id} id={template.id} textValue={template.title}>
                        {template.title}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              <Select
                className="w-full"
                variant="secondary"
                value={workerId}
                onChange={(key) => setWorkerId(String(key ?? ''))}
                placeholder="Выберите исполнителя"
              >
                <FieldLabel>Исполнитель</FieldLabel>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {workers.map((worker) => (
                      <ListBox.Item
                        key={worker.id}
                        id={worker.id}
                        textValue={worker.name ?? worker.login}
                      >
                        {worker.name ?? worker.login}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              <TextField variant="secondary" value={plannedAt} onChange={setPlannedAt}>
                <FieldLabel>Запланирован на</FieldLabel>
                <Input type="datetime-local" />
              </TextField>

              <Select
                className="w-full"
                variant="secondary"
                value={locationId}
                onChange={(key) => setLocationId(String(key ?? ''))}
                placeholder={templateId ? 'Выберите локацию' : 'Сначала выберите шаблон'}
                isDisabled={!templateId}
              >
                <FieldLabel>Локация</FieldLabel>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {locations.map((location) => (
                      <ListBox.Item
                        key={location.id}
                        id={location.id}
                        textValue={`${location.name} (${location.internalId})`}
                      >
                        {location.name} ({location.internalId})
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              {error && <p className="text-sm text-danger">{error}</p>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="tertiary" onPress={onClose}>
              Отмена
            </Button>
            <Button
              onPress={() => void handleSubmit()}
              isDisabled={isCreating || !templateId || !workerId || !locationId}
            >
              {isCreating ? <Spinner size="sm" /> : 'Создать'}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
